
import { Options, Vue, prop } from "vue-class-component";
import ellipsisString from "@/app/infrastructures/misc/common-library/EllipsisString";
import CalculateTimeDifference from "@/app/infrastructures/misc/common-library/CalculateTimeDifference";
import { FlightDetail } from "@/domain/entities/NGen";
import { FlightPlanForECargo } from "@/data/payload/api/CargoApiRequest";
import { ReadyToCargoController } from "@/app/ui/controllers/ReadyToCargoController";
import {
  dateDiffHoursMinute,
  formatDate,
  getDifferenceDate,
  formatDateNumber,
  formatByCountry
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { RtcData } from "@/domain/entities/CargoV2";

class Props {
  data = prop<RtcData>({
    default: new RtcData(),
    type: RtcData
  });
  isDg = prop<boolean>({
    default: false,
    type: Boolean
  });
  isK = prop<boolean>({
    default: false,
    type: Boolean
  });
  isBossPack = prop<boolean>({
    default: false,
    type: Boolean
  });
  isMinimKoli = prop<boolean>({
    default: false,
    type: Boolean
  });
  isMinimWeight = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ['onProcess', 'onChooseSchedule', 'openModal', 'createAndPrint'],
  components: {}
})
export default class CardRTCCargoV2 extends Vue.with(Props) {
  get isCargoPlane(): boolean {
    return this.data.cargoType === "plane";
  }
  
  handleUpdateName(val: string) {
    return ellipsisString(val, 15)
  }

  onEdit() {
    this.$emit("edit", this.data);
  }

  onBookingGenesis() {
    this.$emit("onChooseSchedule", this.data)
  }

  openModal() {
    this.$emit("openModal", this.data)
  }

  createAndPrint(withPrint: boolean, label = false) {
    this.$emit("createAndPrint", { withPrint, label })
  }

  onProcess() {
    this.$emit("onProcess", this.data)
  }

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  // copy from existing
  formatNumber(value: number): string {
    return formatByCountry(value, { type: "weight", country: this.countryCode });
  }
  dateDiffHoursMinute(startDate: any, endDate: any) {
    return dateDiffHoursMinute(startDate, endDate);
  }

  toggleFlightsDetail = false;

  get differenceDate() {
    if (this.data.flightsData.length) {
      return getDifferenceDate(
        this.data.flightsData[0].DepartureDateTime,
        this.data.flightsData[this.data.flightsData.length - 1].ArrivalDateTime
      );
    }
    return "";
  }

  formatDate(value: string): string {
    return formatDate(value);
  }

  created() {
    if (this.isCargoPlane) {
      this.calculateFlightDate(this.data.flightsData);
    }
  }

  isOpenTooltip = false;

  openTooltip() {
    this.isOpenTooltip = true;
  }

  closeTooltip() {
    this.isOpenTooltip = false;
  }

  calculateFlightDate(flightsData: FlightDetail[]) {
    const data: FlightPlanForECargo[] = [];
    flightsData.forEach((key: FlightDetail, index: any) => {
      this.pushDataFlightPlan(key, index, data);
    });
    this.pushDataFlightDate(flightsData, data);
  }

  setCalculateFlightDate(data: FlightPlanForECargo[]) {
    ReadyToCargoController.setCalculateFlightDate(data);
  }

  isDiffDay(dep: any, arr: any): boolean {
    const depTime = +dep.split(":")[0];
    const arrTime = +arr.split(":")[0];
    return arrTime < depTime;
  }

  pushDataFlightPlan(
    key: FlightDetail,
    index: number,
    data: FlightPlanForECargo[]
  ) {
    if (index === 0) {
      data.push(
        new FlightPlanForECargo(
          "",
          true,
          key.FlightNo,
          this.calculateDayFirstFlight("", key.flightDayTransit),
          key.airportCodeOrigin,
          key.airportCodeDestination,
          this.calculateDayFirstFlight(
            // if isMoreThanCutOffTime, 00:00 only dummy data for time is not empty, in calculateDayFirstFlight time will get from nextCargoFlightTime
            this.data.isMoreThanCutOffTime ? "00:00" : key.departureHour,
            key.flightDayTransit
          ),
          this.isDiffDay(key.departureHour, key.arrivalHour)
            ? `${formatDateNumber(
                new Date(
                  this.calculateDayFirstFlight(
                    key.departureHour,
                    key.flightDayTransit
                  )
                ).setDate(
                  new Date(
                    this.calculateDayFirstFlight(
                      key.departureHour,
                      key.flightDayTransit
                    )
                  ).getDate() + 1
                )
              )} ${key.arrivalHour}`
            : this.calculateDayFirstFlight(
                this.data.isMoreThanCutOffTime ? "" : key.arrivalHour,
                key.flightDayTransit
              ),
          this.calculateDayFirstFlight(
            // if isMoreThanCutOffTime, 00:00 only dummy data for time is not empty, in calculateDayFirstFlight time will get from nextCargoFlightTime
            this.data.isMoreThanCutOffTime ? "00:00" : key.departureHour,
            key.flightDayTransit
          ),
          key.convertThresholdTime
        )
      );
    } else {
      data.push(
        new FlightPlanForECargo(
          "",
          true,
          key.FlightNo,
          this.calculateDayTransit("", data, index),
          key.airportCodeOrigin,
          key.airportCodeDestination,
          this.calculateDayTransit(key.departureHour, data, index),
          this.isDiffDay(key.departureHour, key.arrivalHour)
            ? `${formatDateNumber(
                new Date(
                  this.calculateDayTransit(key.departureHour, data, index)
                ).setDate(
                  new Date(
                    this.calculateDayTransit(key.departureHour, data, index)
                  ).getDate() + 1
                )
              )} ${key.arrivalHour}`
            : this.calculateDayTransit(key.arrivalHour, data, index),
          this.calculateDayTransit(key.departureHour, data, index),
          key.convertThresholdTime
        )
      );
    }
  }

  calculateDayTransit(
    time: string,
    data: FlightPlanForECargo[],
    index: number
  ): string {
    let result = "";
    result = `${formatDateNumber(
      new Date(data[index - 1].flight_dep_datetime).setDate(
        new Date(data[index - 1].flight_dep_datetime).getDate() +
          this.data.flightsData[index].flightDayTransit
      )
    )} ${time}`;
    result = !time.length ? result.slice(0, -1) : result;
    return result;
  }

  calculateDayFirstFlight(time: string, flightDayTransit: number): string {
    let result = "";

    const { isMoreThanCutOffTime, nextCargoFlightTime } = this.data;
    if (isMoreThanCutOffTime) {
      result = this.$moment(new Date(nextCargoFlightTime.replace("+07:00", ""))).format(
        time ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD"
      );
    } else {
      result = `${formatDateNumber(
        new Date().setDate(new Date().getDate() + flightDayTransit)
      )} ${time}`;
      result = !time.length ? result.slice(0, -1) : result;
    }
    return result;
  }

  result: FlightPlanForECargo[] = [];

  pushDataFlightDate(flightsData: FlightDetail[], data: FlightPlanForECargo[]) {
    flightsData.forEach((key: FlightDetail, index: number) => {
      data.forEach((secondKey: FlightPlanForECargo, secondIndex: number) => {
        if (index === secondIndex) {
          key.flightDate = secondKey.flight_date;
          key.DepartureDateTime = secondKey.flight_dep_datetime;
          key.ArrivalDateTime = secondKey.flight_arr_datetime;
        }
      });
    });
    this.result = data;
  }

  get onFormatDateEstimationDeparture() {
    return formatDate(
      this.data.flightsData[0]?.DepartureDateTime || this.data.nextCargoFlightTime,
      "DD MMMM YYYY, HH:mm"
    );
  }

  get dateNow() {
    return this.$moment().format("YYYY-MM-DDTHH:mm:ss");
  }

  get formatCalculateDiffTime() {
    return CalculateTimeDifference(
      this.dateNow,
      this.data.flightsData[0]?.DepartureDateTime || this.data.nextCargoFlightTime
    );
  }
}
